<script setup>
import { onMounted, provide, ref, watch } from "vue";
import { cloneDeep } from "lodash";
import { useRenderContext } from "~/composables/useRenderContext";

useRenderContext("form");

const emit = defineEmits(["submit", "change"]);
const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({}),
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
});

const formData = ref(cloneDeep(props.initialData));
provide("formData", formData);
watch(
  formData,
  () => {
    emit("change", formData.value);
  },
  { deep: true }
);

const formRef = ref(null);
onMounted(() => {
  if (formRef.value && !formRef.value.contains(document.activeElement)) {
    // `preventScroll: true` stops Firefox from scrolling the freshly focused
    // first field into view on open, which could otherwise push other required
    // fields (e.g. the date column in the create-stay modal) off-screen. It is a
    // no-op in Chrome, where the first field of a freshly opened form is already
    // visible at the top of the scroll container.
    formRef.value.querySelector("input, select, textarea")?.focus({ preventScroll: true });
  }
});

function submit() {
  emit("submit", formData.value);
}

// A button outside the form (a modal footer holding several forms) has to
// submit it imperatively, which skips the constraint validation the browser
// runs before firing a submit event of its own. Without this an empty required
// field reaches the server as a missing GraphQL variable.
function submitFromOutsideTheForm() {
  if (formRef.value && !formRef.value.reportValidity()) return;

  submit();
}
defineExpose({ submit: submitFromOutsideTheForm });
</script>

<template>
  <form ref="formRef" @submit.prevent="submit">
    <BaseValidationErrorsBoundary :errors="props.errors">
      <slot />
    </BaseValidationErrorsBoundary>
  </form>
</template>
